import React from 'react';
import { Button, Dialog, DialogTrigger, DialogContent, DialogFooter, Flex, Text, LoadingOrbit } from '@chatsdet/ui';
import type { ApplicationContextItem, ApplicationContextSection } from '../types';
import { useContextHistory } from '../useApplicationContext';

const SOURCE_LABELS: Record<string, string> = {
  bootstrap: 'Bootstrap',
  workflow: 'Workflow',
  manual: 'Manual',
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  section: ApplicationContextSection | null;
  itemKey: string | null;
  itemLabel: string | null;
  onRestore: (item: ApplicationContextItem) => void;
};

export function VersionHistoryDialog({
  isOpen,
  onClose,
  propertyId,
  section,
  itemKey,
  itemLabel,
  onRestore,
}: Props) {
  const historyQ = useContextHistory(propertyId, section, itemKey);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Button className="hidden">Trigger</Button>
      <Dialog
        title={`History: ${itemLabel ?? 'Item'}`}
        description="View all versions of this context item"
      >
        <DialogContent>
          <div className="max-h-[400px] overflow-y-auto divide-y divide-border border-y border-border">
            {historyQ.isLoading && (
              <div className="flex justify-center py-8">
                <LoadingOrbit size="md" />
              </div>
            )}

            {historyQ.data?.map((version) => (
              <div
                key={version.id}
                className="p-3"
              >
                <Flex align="center" justify="between" className="mb-1.5">
                  <Flex align="center" gap="sm">
                    <Text size="sm" weight="medium" className="text-foreground">
                      Version {version.version}
                    </Text>
                    <Text size="xs" className="text-muted-foreground">
                      {SOURCE_LABELS[version.source] ?? version.source}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="sm">
                    <Text size="xs" className="text-muted-foreground">
                      {new Date(version.createdAt).toLocaleString()}
                    </Text>
                    {version.version !== historyQ.data?.[0]?.version && (
                      <Button variant="ghost" size="sm" onPress={() => onRestore(version)}>
                        Restore
                      </Button>
                    )}
                  </Flex>
                </Flex>
                <Text size="sm" className="text-muted-foreground whitespace-pre-wrap">
                  {version.content}
                </Text>
              </div>
            ))}

            {historyQ.data?.length === 0 && (
              <Text size="sm" className="text-muted-foreground text-center py-4">
                No version history found
              </Text>
            )}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={onClose}>
            Close
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
